/**
 * useGlobalRevealObserver — single IntersectionObserver that auto-wires any
 * element with a `data-reveal` attribute on the page. Mounts on route change
 * and also watches document.body via MutationObserver so async-fetched
 * subtrees (editorial cards, report sections, article body) are picked up
 * after their fetches resolve.
 *
 * Important: the HIDDEN starting state for `[data-reveal]` elements is
 * applied by CSS attribute selectors in index.css, so the very first paint
 * already shows them hidden. This hook's only job is to toggle the
 * `is-visible` class at the right moment, which then triggers the CSS
 * transition. We never need to add the "hidden" classes via JS — that's the
 * trick that avoids the "snap-to-final" race where the browser paints the
 * element in its final state once before the transition can start.
 *
 * Above-the-fold elements get `is-visible` on the next animation frame so
 * the slide/rise plays immediately after mount. Below-the-fold elements
 * wait for IntersectionObserver. A scroll-jump catch-up sweep also reveals
 * elements the user has scrolled past without crossing the IO boundary
 * (anchor links, programmatic scrollTo, End key, etc.).
 *
 * Usage:
 *   <section data-reveal>...</section>           ← defaults to "rise"
 *   <div data-reveal="fade">...</div>
 *   <div data-reveal="slide-left">...</div>
 *   <div data-reveal="emphasize" data-reveal-delay="2">...</div>
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

function isAboveTheFold(el) {
    if (typeof window === "undefined") return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 0;
    // Already at least partially in (or near) the initial viewport — treat
    // as "should reveal immediately on mount" so no flash of hidden content.
    return rect.top < vh * 0.95 && rect.bottom > 0;
}

function initElement(el, io) {
    if (el.hasAttribute("data-reveal-init")) return;
    // Don't re-process elements that already revealed in a previous effect run.
    if (el.classList.contains("is-visible")) {
        el.setAttribute("data-reveal-init", "1");
        return;
    }
    el.setAttribute("data-reveal-init", "1");

    if (isAboveTheFold(el)) {
        // CSS already painted the element in its hidden state on the very
        // first paint (via attribute selectors). All we have to do is wait
        // one animation frame so the transition has a "before" state to
        // animate FROM, then toggle `is-visible`. A single rAF is enough
        // because the hidden state was already painted by the browser when
        // React first rendered the element — we don't need to force a
        // reflow ourselves.
        requestAnimationFrame(() => {
            el.classList.add("is-visible");
        });
    } else {
        // Mark as IO-observed so the scroll catch-up sweep only touches
        // these (and never preempts above-the-fold reveal kickoffs).
        el.setAttribute("data-reveal-observed", "1");
        io.observe(el);
    }
}

export default function useGlobalRevealObserver() {
    const location = useLocation();

    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") return;

        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting) {
                        e.target.classList.add("is-visible");
                        io.unobserve(e.target);
                    }
                }
            },
            // Fire as soon as ANY pixel of the element touches the observation
            // box, and extend the box generously below the viewport so reveals
            // play just before they're scrolled into view. The wide bottom
            // margin also gives the IO a chance to fire even on fast/instant
            // scrollTo jumps that would otherwise skip past elements before
            // the callback queue flushes.
            { threshold: 0, rootMargin: "0px 0px 800px 0px" },
        );

        // Scroll-jump catch-up: IO only fires on intersection-state transitions,
        // so if the user (or a programmatic `scrollTo`, anchor link, End key,
        // smooth-scroll, etc.) skips PAST an unrevealed element, the IO never
        // sees it enter the viewport — it just moves from "below viewport" to
        // "above viewport" with intersectionRatio=0 the whole way through, and
        // gets stuck at opacity:0. On every scroll we sweep any
        // still-unrevealed observed elements and reveal those whose top edge
        // is now above the viewport (i.e., the user has scrolled past them).
        let scrollRaf = 0;
        const catchUpSweep = () => {
            const vh = window.innerHeight || 0;
            // Reveal anything within or above the viewport bottom that's still
            // hidden. We deliberately query EVERY data-reveal element here
            // (not just `[data-reveal-observed='1']`) so this also catches
            // elements that the initial-scan classified as below-the-fold
            // before IO had a chance to attach, plus any below-fold cards
            // the user scrolled past in a single instant scrollTo.
            const nodes = document.querySelectorAll(
                "[data-reveal]:not(.is-visible)",
            );
            for (const el of nodes) {
                const rect = el.getBoundingClientRect();
                if (rect.top < vh && rect.bottom > 0) {
                    // Currently in viewport — reveal now.
                    el.classList.add("is-visible");
                    if (el.hasAttribute("data-reveal-observed")) {
                        io.unobserve(el);
                    }
                } else if (rect.bottom <= 0) {
                    // Already scrolled past — reveal without animation flourish
                    // so the user sees finished content when they scroll back.
                    el.classList.add("is-visible");
                    if (el.hasAttribute("data-reveal-observed")) {
                        io.unobserve(el);
                    }
                }
            }
        };
        const handleScroll = () => {
            if (scrollRaf) return;
            scrollRaf = requestAnimationFrame(() => {
                scrollRaf = 0;
                catchUpSweep();
            });
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll, { passive: true });

        const scan = () => {
            document
                .querySelectorAll("[data-reveal]:not([data-reveal-init])")
                .forEach((el) => initElement(el, io));
        };

        // Initial scan + retry schedule for async-fetched subtrees that
        // typically resolve within ~1.5s of route mount.
        scan();
        const t1 = setTimeout(scan, 200);
        const t2 = setTimeout(scan, 800);
        const t3 = setTimeout(scan, 1800);
        // Hard safety net: 2.4s after mount, force-reveal anything still
        // hidden that's within the viewport (regardless of whether IO fired).
        // This catches edge cases like late-mounted content, programmatic
        // scrollTo before IO attached, and browsers where IO entries get
        // collapsed during fast/instant scroll sequences.
        const t4 = setTimeout(catchUpSweep, 2400);
        // Continuous polling safety net for the first 6 seconds of the route.
        // Cheap (single rAF read per tick) and bullet-proofs the system
        // against any IO/scroll event gotchas (e.g., Playwright instant
        // scrollTo not firing scroll, browsers collapsing IO entries during
        // momentum scroll, programmatic scrolls from anchor links, etc.).
        const pollInterval = setInterval(catchUpSweep, 800);
        const t5 = setTimeout(() => clearInterval(pollInterval), 6000);

        // Long-tail safety net: any node mounted later (infinite scroll,
        // modals, deferred admin tiles) still gets picked up.
        let mo = null;
        if (typeof MutationObserver !== "undefined") {
            mo = new MutationObserver((mutations) => {
                for (const m of mutations) {
                    for (const node of m.addedNodes) {
                        if (node.nodeType !== 1) continue;
                        if (
                            node.matches?.("[data-reveal]:not([data-reveal-init])")
                        ) {
                            initElement(node, io);
                        }
                        node
                            .querySelectorAll?.(
                                "[data-reveal]:not([data-reveal-init])",
                            )
                            .forEach((el) => initElement(el, io));
                    }
                }
            });
            mo.observe(document.body, { childList: true, subtree: true });
        }

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            clearTimeout(t4);
            clearTimeout(t5);
            clearInterval(pollInterval);
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
            if (scrollRaf) cancelAnimationFrame(scrollRaf);
            mo?.disconnect();
            io.disconnect();
            // Clear init markers so a re-mount (React StrictMode double-effect,
            // SPA route change, etc.) can re-attach the new IO to elements that
            // hadn't fired yet on the disconnected one. Already-revealed
            // (is-visible) elements stay visible — we just allow the new effect
            // to skip them via that class check.
            document
                .querySelectorAll("[data-reveal-init]")
                .forEach((el) => {
                    el.removeAttribute("data-reveal-init");
                    el.removeAttribute("data-reveal-observed");
                });
        };
    }, [location.pathname]);
}
